import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

// ── Auto-Refresh Interceptor ─────────────────────────────────────────────
// Wenn ein Aufruf mit 401 fehlschlägt, versuchen wir EINMAL /auth/refresh
// (der Refresh-Token-Cookie lebt 7 Tage) und wiederholen den ursprünglichen
// Request. So bleibt der Nutzer über Refreshs und lange Sessions eingeloggt,
// obwohl das Access-Token nur 15 Minuten gültig ist.
let refreshInFlight = null;
axios.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error?.config;
    const status = error?.response?.status;
    if (
      status !== 401 ||
      !original ||
      original._retry ||
      // Der /auth/me und /auth/refresh Aufruf selbst darf nicht endlos loopen
      original.url?.endsWith("/auth/refresh") ||
      original.url?.endsWith("/auth/login")
    ) {
      return Promise.reject(error);
    }
    original._retry = true;
    try {
      refreshInFlight = refreshInFlight || axios.post(`${API}/auth/refresh`);
      await refreshInFlight;
    } catch (e) {
      refreshInFlight = null;
      return Promise.reject(error);
    } finally {
      // In-flight nach Auflösung wieder freigeben, damit spätere 401 erneut
      // refreshen können.
      setTimeout(() => { refreshInFlight = null; }, 0);
    }
    return axios(original);
  }
);

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function fmtErr(d) {
  if (!d) return "Etwas ist schiefgelaufen.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return d?.msg || String(d);
}

// LocalStorage-Fallback: sofort beim ersten Paint anwenden, bevor /me antwortet
const LS_KEY = "trademind:prefs";
function readCachedPrefs() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return { theme: "dark", compact: false };
    const p = JSON.parse(raw);
    return {
      theme: p.theme === "light" ? "light" : "dark",
      compact: !!p.compact,
    };
  } catch { return { theme: "dark", compact: false }; }
}
function writeCachedPrefs(p) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(p)); } catch {}
}
function applyPrefs({ theme, compact }) {
  const html = document.documentElement;
  html.classList.toggle("light", theme === "light");
  html.classList.toggle("compact", !!compact);
}

// Beim Modul-Load sofort die gecachten Prefs anwenden — verhindert Theme-Flash
applyPrefs(readCachedPrefs());

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=anon, obj=auth
  const [state, setState] = useState("unbekannt");
  const [prefs, setPrefs] = useState(readCachedPrefs);
  const [simMode, setSimMode] = useState(() => {
    try { return sessionStorage.getItem("trademind:sim-newuser") === "1"; } catch { return false; }
  });
  const [simStage, setSimStage] = useState(() => {
    try {
      const s = sessionStorage.getItem("trademind:sim-stage");
      return ["orientierung", "struktur", "optimierung"].includes(s) ? s : "orientierung";
    } catch { return "orientierung"; }
  });

  const check = useCallback(async () => {
    setState("anmeldung-laeuft");
    try {
      const r = await axios.get(`${API}/auth/me`);
      setUser(r.data);
      setState("angemeldet");
      const p = r.data?.prefs || { theme: "dark", compact: false };
      setPrefs(p);
      writeCachedPrefs(p);
      applyPrefs(p);
    } catch {
      setUser(false);
      setState("nicht-angemeldet");
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  // Neu-User-Simulation für Admins: überlagert Onboarding-/Quiz-/Tour-Signale,
  // ohne die DB anzurühren. Purely client-side. Session-scoped, damit ein
  // Reload den Simulationsmodus behält, ein Logout ihn aber verlässt.
  //
  // Der Admin kann eine der drei Nutzergruppen (Orientierung/Struktur/
  // Optimierung) simulieren. Für „orientierung" wird zusätzlich das Wissens-
  // Profil zurückgesetzt, damit Quiz-Banner + Onboarding erscheinen.
  const enterSim = (stage = "orientierung") => {
    const valid = ["orientierung", "struktur", "optimierung"].includes(stage) ? stage : "orientierung";
    try {
      // Nur beim Orientierungs-Modus die Dismissals löschen — sonst nervt der
      // Wechsel zwischen Stages mit ständig neuen Bannern.
      if (valid === "orientierung") {
        localStorage.removeItem("trademind:quiz-banner-hidden-at");
        localStorage.removeItem("calm-trading.onboarding.v1");
        localStorage.removeItem("trademind:tour-step");
        localStorage.removeItem("trademind:tour-later-at");
      }
      sessionStorage.setItem("trademind:sim-newuser", "1");
      sessionStorage.setItem("trademind:sim-stage", valid);
    } catch {}
    setSimStage(valid);
    setSimMode(true);
  };
  const exitSim = () => {
    try {
      sessionStorage.removeItem("trademind:sim-newuser");
      sessionStorage.removeItem("trademind:sim-stage");
    } catch {}
    setSimMode(false);
  };

  // Für Konsument:innen ein User-Objekt aufsetzen, das wie ein frischer Account
  // in der jeweiligen Stage aussieht — reale Daten bleiben in `realUser` erhalten.
  const effectiveUser = (simMode && user && user !== false)
    ? (simStage === "orientierung"
        ? {
            ...user,
            onboarding: { stage: "orientierung", goal: null, answered_at: null },
            knowledge_profile: {},
            coach_turn_count: 0,
            explained_terms: [],
            tour: null,
          }
        : {
            ...user,
            onboarding: { ...(user.onboarding || {}), stage: simStage },
          })
    : user;

  const login = async (email, password) => {
    const r = await axios.post(`${API}/auth/login`, { email, password });
    setUser(r.data);
    setState("angemeldet");
    await check(); // Prefs frisch nachladen
    return r.data;
  };
  const logout = async () => {
    setState("abmeldung-laeuft");
    try { await axios.post(`${API}/auth/logout`); } catch {}
    try { sessionStorage.removeItem("trademind:sim-newuser"); } catch {}
    setSimMode(false);
    setUser(false);
    setState("nicht-angemeldet");
    // Prefs behalten (LS) — sonst würde beim Logout das Theme zurückspringen.
  };
  const refresh = check;

  const updatePrefs = async (patch) => {
    // Optimistic UI: sofort anwenden
    const next = { ...prefs, ...patch };
    setPrefs(next);
    writeCachedPrefs(next);
    applyPrefs(next);
    try {
      const r = await axios.patch(`${API}/auth/me/prefs`, patch);
      const server = r.data?.prefs || next;
      setPrefs(server);
      writeCachedPrefs(server);
      applyPrefs(server);
      setUser((u) => (u ? { ...u, prefs: server } : u));
      return server;
    } catch (e) {
      // Rollback bei Fehler
      setPrefs(prefs);
      writeCachedPrefs(prefs);
      applyPrefs(prefs);
      throw e;
    }
  };

  return (
    <AuthContext.Provider value={{ user: effectiveUser, realUser: user, state, prefs, login, logout, refresh, updatePrefs, isAdmin: user?.role === "admin", simMode, simStage, enterSim, exitSim }}>
      {children}
    </AuthContext.Provider>
  );
}
